import { memo, Fragment, useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import CategorizedListRoot from "../CategorizedListRoot";
import { getLomake } from "@/services/lomakkeet/index";
import { useIntl } from "react-intl";
import ExpandableRowRoot from "../ExpandableRowRoot";
import common from "@/i18n/definitions/common";
import { has, isEmpty, length, omit } from "ramda";
import { useLomakedata } from "@/stores/lomakedata";
import { getReducedStructure } from "@/utils/lomakkeet";
import FormTitle from "@/components/00-atoms/FormTitle";
import { getReducedStructureIncludingChanges } from "./utils";
import equal from "react-fast-compare";
import { useChangeObjects } from "@/stores/muutokset";

const defaultProps = {
  aktiivisetTutkinnot: undefined,
  onDeselectAll: () => {},
  onSelectAll: () => {},
  selectAll: false,
  changeObjects: [],
  data: {},
  functions: {},
  isInExpandableRow: true,
  isPreviewModeOn: false,
  isReadOnly: false,
  isDisabled: false,
  isRowExpanded: false,
  noPadding: false,
  isSavingState: true,
  prefix: "",
  rowMessages: {},
  rowTitle: "",
  showCategoryTitles: true,
  showValidationErrors: true,
  uncheckParentWithoutActiveChildNodes: false
};

const Lomake = memo(
  ({
    aktiivisetTutkinnot = defaultProps.aktiivisetTutkinnot,
    onDeselectAll = defaultProps.onDeselectAll(),
    onSelectAll = defaultProps.onSelectAll(),
    selectAll = defaultProps.selectAll,
    selectAllText = defaultProps.selectAll,
    anchor,
    changeObjects = defaultProps.changeObjects,
    code,
    data = defaultProps.data,
    formTitle,
    functions = defaultProps.functions,
    isInExpandableRow = defaultProps.isInExpandableRow,
    isPreviewModeOn = defaultProps.isPreviewModeOn,
    isDisabled = defaultProps.isDisabled,
    isReadOnly = defaultProps.isReadOnly,
    isRowExpanded = defaultProps.isRowExpanded,
    isSavingState = defaultProps.isSavingState,
    lomakedataAnchor,
    mode,
    path: _path,
    prefix = defaultProps.prefix,
    runInsteadOfSettingChanges,
    showCategoryTitles = defaultProps.showCategoryTitles,
    uncheckParentWithoutActiveChildNodes = defaultProps.uncheckParentWithoutActiveChildNodes,
    noPadding = defaultProps.noPadding,
    rowMessages = defaultProps.rowMessages,
    rowTitle = defaultProps.rowTitle,
    showValidationErrors = defaultProps.showValidationErrors,
    titleLevel,
    validationErrors
  }) => {
    const intl = useIntl();

    const _titleLevel = titleLevel || (isPreviewModeOn ? 3 : 2);

    const rowLocalizations = isEmpty(rowMessages)
      ? {
          undo: intl.formatMessage(common.undo),
          changesTest: intl.formatMessage(common.changesText)
        }
      : rowMessages;

    const [{ focusOn }, { setFocusOn, setChanges }] = useChangeObjects();

    const [, lomakedataActions] = useLomakedata({
      anchor: lomakedataAnchor || anchor
    });

    const [lomake, setLomake] = useState();

    const onChangesRemove = useCallback(
      anchor => {
        setChanges([], anchor);
      },
      [setChanges]
    );

    const onChangesUpdate = useCallback(
      ({ anchor, changes, targetNode }) => {
        // setLatestTargetNode(targetNode);
        if (runInsteadOfSettingChanges) {
          runInsteadOfSettingChanges(changes, anchor, targetNode);
        } else {
          setChanges(changes, anchor, targetNode.original.fullAnchor);
        }
      },
      [setChanges]
    );

    const onSelectAllTrigger = useCallback(
      ({ anchor }) => {
        // Raw unfiltered data
        const selectAllResultChangeObjects = onSelectAll(aktiivisetTutkinnot);

        // Filter out null values
        const filteredChangeObjects = selectAllResultChangeObjects.filter(
          item => item !== null
        );

        if (filteredChangeObjects.length > 0) {
          setChanges(filteredChangeObjects, anchor, undefined);
        }
      },
      [setChanges, aktiivisetTutkinnot]
    );

    const onDeselectAllTrigger = useCallback(
      ({ anchor }) => {
        // Raw unfiltered data
        const deselectedEnglishChangeObjects =
          onDeselectAll(aktiivisetTutkinnot);

        // Filter out null values
        const filteredChangeObjects = deselectedEnglishChangeObjects.filter(
          item => item !== null
        );

        if (filteredChangeObjects.length > 0) {
          setChanges(filteredChangeObjects, anchor, undefined);
        }
      },
      [setChanges, aktiivisetTutkinnot]
    );

    const onFocus = useCallback(
      (anchor = null) => {
        setFocusOn(anchor);
      },
      [setFocusOn]
    );

    useEffect(() => {
      (async () => {
        async function fetchLomake(_mode, _changeObjects, _data, _functions) {
          return await getLomake(
            _mode || mode,
            _changeObjects || changeObjects,
            _data || data,
            _functions || functions,
            { isPreviewModeOn, isReadOnly, isDisabled },
            intl.locale,
            _path,
            prefix
          );
        }

        const juuriHaettuLomake = await fetchLomake();
        const lomakerakenne = juuriHaettuLomake.structure || juuriHaettuLomake;

        const lomakedata =
          lomakerakenne && lomakerakenne.length
            ? getReducedStructureIncludingChanges(
                lomakedataAnchor || anchor,
                getReducedStructure(lomakerakenne),
                changeObjects
              )
            : [];

        /**
         * Osa lomakkeista voi olla sellaisia, että niiden tilan
         * tallentaminen aiheuttaa liikaa uudelleen lataamisia.
         * Esimerkkinä tällaisesta lomakkeesta on rajoitelomake.
         * Rajoitelomake hyödyntää lomakedataa ja näin ollen
         * päivittyy datan päivittyessä. Jos rajoitelomakkeen
         * dataa tallennetaan samaan paikkaan, kuin muiden
         * lomakkeiden, aiheutuu siitä ikiluuppi.
         */
        if (isSavingState) {
          lomakedataActions.setLomakedata(lomakedata, anchor);
        }
        /**
         * Esikatselulomake tarvitsee lomakerakenteen luontia varten
         * muokkaustilaisen lomakkeen nykytilan.
         */
        if (isPreviewModeOn) {
          const previewLomake = await fetchLomake("preview", [], {
            ...data,
            lomakedata
          });
          setLomake(previewLomake.structure || previewLomake);
        } else {
          if (has("isValid", juuriHaettuLomake)) {
            lomakedataActions.setValidity(juuriHaettuLomake.isValid, anchor);
          }

          setLomake(lomakerakenne);
        }
      })();
    }, [
      _path,
      anchor,
      changeObjects,
      data,
      functions,
      intl.locale,
      isPreviewModeOn,
      isReadOnly,
      isSavingState,
      lomakedataActions,
      lomakedataAnchor,
      mode,
      prefix,
      validationErrors
    ]);

    if (Array.isArray(lomake) && lomake.length) {
      return (
        <Fragment>
          {code || formTitle ? (
            <FormTitle
              code={isPreviewModeOn || !code ? null : code}
              isPreviewModeOn={isPreviewModeOn}
              level={_titleLevel}
              title={formTitle}
            />
          ) : null}
          {isInExpandableRow &&
          !isPreviewModeOn &&
          (mode !== "reasoning" || length(changeObjects)) ? (
            <ExpandableRowRoot
              anchor={anchor}
              selectAll={selectAll}
              onDeselectAll={onDeselectAllTrigger}
              onSelectAll={onSelectAllTrigger}
              selectAllText={selectAllText}
              changes={changeObjects}
              key={`expandable-row-root`}
              hideAmountOfChanges={true}
              isExpanded={isRowExpanded}
              onChangesRemove={onChangesRemove}
              messages={rowLocalizations}
              sectionId={anchor}
              title={rowTitle}>
              <div className={noPadding ? "" : "p-8"}>
                <CategorizedListRoot
                  anchor={anchor}
                  focusOn={focusOn}
                  categories={lomake}
                  changes={changeObjects}
                  isReadOnly={isReadOnly}
                  onFocus={onFocus}
                  onUpdate={onChangesUpdate}
                  showCategoryTitles={showCategoryTitles}
                  showValidationErrors={showValidationErrors}
                  uncheckParentWithoutActiveChildNodes={
                    uncheckParentWithoutActiveChildNodes
                  }
                  validationErrors={validationErrors}
                />
              </div>
            </ExpandableRowRoot>
          ) : mode !== "reasoning" || length(changeObjects) ? (
            <CategorizedListRoot
              anchor={anchor}
              focusOn={focusOn}
              categories={lomake}
              isPreviewModeOn={isPreviewModeOn}
              isReadOnly={isReadOnly}
              changes={changeObjects}
              onFocus={onFocus}
              onUpdate={onChangesUpdate}
              showCategoryTitles={showCategoryTitles}
              showValidationErrors={showValidationErrors}
              uncheckParentWithoutActiveChildNodes={
                uncheckParentWithoutActiveChildNodes
              }
              validationErrors={validationErrors}
            />
          ) : null}
        </Fragment>
      );
    } else {
      return null;
    }
  },
  (cp, np) => {
    return equal(omit(["functions"], cp), omit(["functions"], np));
  }
);

Lomake.propTypes = {
  anchor: PropTypes.string,
  changeObjects: PropTypes.array,
  code: PropTypes.string,
  data: PropTypes.object,
  formTitle: PropTypes.string,
  functions: PropTypes.object,
  isInExpandableRow: PropTypes.bool,
  isPreviewModeOn: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  isRowExpanded: PropTypes.bool,
  isSavingState: PropTypes.bool,
  lomakedataAnchor: PropTypes.string,
  mode: PropTypes.string,
  noPadding: PropTypes.bool,
  path: PropTypes.array,
  prefix: PropTypes.string,
  rowMessages: PropTypes.object,
  rowTitle: PropTypes.string,
  runInsteadOfSettingChanges: PropTypes.func,
  showCategoryTitles: PropTypes.bool,
  showValidationErrors: PropTypes.bool,
  titleLevel: PropTypes.number,
  uncheckParentWithoutActiveChildNodes: PropTypes.bool,
  validationErrors: PropTypes.array
};

Lomake.displayName = "Lomake";

export default Lomake;
